/**
 * Notification Settings Modal Component
 */

import React, { useState, useEffect, useCallback } from "react";
import { X, Settings, Save, RotateCcw } from "lucide-react";
import {
  notificationAPI,
  NotificationSettings,
} from "../../services/notificationAPI";
import { useAppSelector } from "../../store/hooks";
import { logError } from "../../utils/errorLogger";

interface NotificationSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  theme: "dark" | "light";
}

interface NotificationCategory {
  title: string;
  items: {
    key: keyof NotificationSettings;
    label: string;
    description: string;
  }[];
}

// Admin notification categories
const ADMIN_CATEGORIES: NotificationCategory[] = [
  {
    title: "Event Approvals",
    items: [
      {
        key: "event_created",
        label: "New Events Awaiting Approval",
        description: "When hosts create events that need approval",
      },
      {
        key: "event_updated",
        label: "Event Changes",
        description: "When events are modified",
      },
    ],
  },
  {
    title: "Guest Activity",
    items: [
      {
        key: "guest_response",
        label: "Guest Responses",
        description: "When guests respond to invitations (monitoring)",
      },
    ],
  },
  {
    title: "Reminders",
    items: [
      {
        key: "event_reminder",
        label: "Event Reminders",
        description: "Reminders for upcoming events",
      },
    ],
  },
];

// Host notification categories
const HOST_CATEGORIES: NotificationCategory[] = [
  {
    title: "My Events",
    items: [
      {
        key: "event_approved",
        label: "Event Approved",
        description: "When your events are approved",
      },
      {
        key: "event_rejected",
        label: "Event Rejected",
        description: "When your events are rejected",
      },
      {
        key: "event_updated",
        label: "Event Updated",
        description: "When your events are modified",
      },
    ],
  },
  {
    title: "Guest Activity",
    items: [
      {
        key: "guest_response",
        label: "Guest Responses",
        description: "When guests respond to your invitations",
      },
    ],
  },
  {
    title: "Reminders",
    items: [
      {
        key: "event_reminder",
        label: "Event Reminders",
        description: "Reminders for upcoming events",
      },
    ],
  },
];

export const NotificationSettingsModal: React.FC<
  NotificationSettingsModalProps
> = ({ isOpen, onClose, theme }) => {
  const { user } = useAppSelector((state) => state.auth);
  const userRole = user?.role || "host";

  const [settings, setSettings] = useState<NotificationSettings>({
    event_created: true,
    event_approved: true,
    event_rejected: true,
    event_updated: true,
    guest_response: true,
    event_reminder: true,
    system_alert: true,
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load settings when modal opens


  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await notificationAPI.getSettings();
      setSettings(response.settings);
    } catch (err: any) {
      logError("Error loading notification settings", err);
      setError("Failed to load notification settings");
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies needed

  useEffect(() => {
    if (isOpen) {
      loadSettings();
      document.body.style.overflow = "hidden";
    }
  
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen, loadSettings]); 

  const handleToggle = (key: keyof NotificationSettings) => {
    // Don't allow toggling system_alert
    if (key === "system_alert") {
      return;
    }

    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      await notificationAPI.updateSettings(settings);
      onClose();
    } catch (err: any) {
      logError("Error saving notification settings", err);
      setError("Failed to save notification settings");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    setError(null);

    try {
      const response = await notificationAPI.resetSettings();
      setSettings(response.settings);
    } catch (err: any) {
      logError("Error resetting notification settings", err);
      setError("Failed to reset notification settings");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black bg-opacity-50"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        className={`w-full max-w-2xl mx-2 sm:mx-4 rounded-xl shadow-2xl ${
          theme === "dark"
            ? "bg-gray-800 border-gray-700"
            : "bg-white border-gray-200"
        } border max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col`}
        style={{
          pointerEvents: "auto",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`px-4 sm:px-6 py-3 sm:py-4 border-b ${
            theme === "dark" ? "border-gray-700" : "border-gray-200"
          } flex items-start sm:items-center justify-between gap-3`}
        >
          <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
            <div
              className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${
                theme === "dark" ? "bg-[#33FF00]" : "bg-[#33FF00]/20"
              }`}
            >
              <Settings
                className={`h-4 w-4 sm:h-5 sm:w-5 ${
                  theme === "dark" ? "text-black" : "text-[#33FF00]"
                }`}
              />
            </div>
            <div className="min-w-0 flex-1">
              <h2
                className={`text-base sm:text-lg font-semibold ${
                  theme === "dark" ? "text-white" : "text-black"
                }`}
              >
                Notification Settings
              </h2>
              <p
                className={`text-xs sm:text-sm ${
                  theme === "dark" ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Choose which notifications you want to receive
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-1.5 sm:p-2 rounded-lg transition-colors flex-shrink-0 ${
              theme === "dark"
                ? "text-gray-400 hover:text-white hover:bg-gray-700"
                : "text-gray-500 hover:text-black hover:bg-gray-100"
            }`}
          >
            <X className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-3 sm:py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8 sm:py-12">
              <div
                className={`text-xs sm:text-sm ${
                  theme === "dark" ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Loading settings...
              </div>
            </div>
          ) : error ? (
            <div
              className={`py-3 sm:py-4 px-3 sm:px-4 rounded-lg text-xs sm:text-sm ${
                theme === "dark"
                  ? "bg-red-900/20 text-red-400"
                  : "bg-red-50 text-red-600"
              }`}
            >
              {error}
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              {(userRole === "admin" ? ADMIN_CATEGORIES : HOST_CATEGORIES).map(
                (category) => (
                  <div key={category.title}>
                    <h3
                      className={`text-xs sm:text-sm font-semibold mb-2 sm:mb-3 ${
                        theme === "dark" ? "text-white" : "text-black"
                      }`}
                    >
                      {category.title}
                    </h3>
                    <div className="space-y-2 sm:space-y-3">
                      {category.items.map((item) => (
                        <div
                          key={item.key}
                          className={`p-3 sm:p-4 rounded-lg border ${
                            theme === "dark"
                              ? "border-gray-700 bg-gray-800"
                              : "border-gray-200 bg-gray-50"
                          } ${item.key === "system_alert" ? "opacity-60" : ""}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <label
                                htmlFor={item.key}
                                className={`block text-xs sm:text-sm font-medium ${
                                  theme === "dark" ? "text-white" : "text-black"
                                }`}
                              >
                                {item.label}
                                {item.key === "system_alert" && (
                                  <span
                                    className={`ml-1 sm:ml-2 text-xs ${
                                      theme === "dark"
                                        ? "text-gray-400"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    (Always enabled)
                                  </span>
                                )}
                              </label>
                              <p
                                className={`text-xs mt-1 ${
                                  theme === "dark"
                                    ? "text-gray-400"
                                    : "text-gray-600"
                                }`}
                              >
                                {item.description}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleToggle(item.key)}
                              disabled={item.key === "system_alert"}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                                settings[item.key]
                                  ? "bg-[#33FF00]"
                                  : theme === "dark"
                                  ? "bg-gray-700"
                                  : "bg-gray-300"
                              } ${
                                item.key === "system_alert"
                                  ? "cursor-not-allowed"
                                  : "cursor-pointer"
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  settings[item.key]
                                    ? "translate-x-6"
                                    : "translate-x-1"
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`px-4 sm:px-6 py-3 sm:py-4 border-t ${
            theme === "dark" ? "border-gray-700" : "border-gray-200"
          } flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3`}
        >
          <button
            onClick={handleReset}
            disabled={saving}
            className={`flex items-center justify-center sm:justify-start space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-colors text-xs sm:text-sm ${
              theme === "dark"
                ? "text-gray-400 hover:text-white hover:bg-gray-700"
                : "text-gray-600 hover:text-black hover:bg-gray-100"
            } ${saving ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <RotateCcw className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="font-medium">Reset to Defaults</span>
          </button>

          <div className="flex items-center space-x-2 sm:space-x-3">
            <button
              onClick={onClose}
              className={`px-3 sm:px-4 py-2 rounded-lg transition-colors text-xs sm:text-sm font-medium ${
                theme === "dark"
                  ? "text-gray-300 hover:bg-gray-700"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`flex items-center space-x-2 px-3 sm:px-4 py-2 rounded-lg text-black bg-gradient-to-r from-[#33FF00] to-[#2BD400] hover:from-[#2BD400] hover:to-[#33FF00] transition-colors text-xs sm:text-sm ${
                saving ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <Save className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="font-medium">
                {saving ? "Saving..." : "Save Changes"}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettingsModal;
